import global from "../../../common/utility/global";
import {
    COLORES_CATEGORIA,
    COLORES_ESTADO,
    COLORES_ESTADO_OTROS,
    ESTILOS_ESTABLECIMIENTO,
    NOMBRES_MES_CORTO,
} from "./constants";

export function formatCurrency(value, moneda = "PEN") {
    const numero = Number(value) || 0;
    const simbolo = moneda === "USD" ? "US$" : "S/";
    return `${simbolo} ${global.decimalFormat(numero.toFixed(2))}`;
}

export function formatNumber(value, decimales = 1) {
    const numero = Number(value) || 0;
    return global.decimalFormat(numero.toFixed(decimales));
}

const MAX_CARACTERES_NOMBRE = 18;

// Recorta nombres largos para que la leyenda/tooltip mantengan siempre el
// mismo ancho; sin esto, un nombre muy largo estira la leyenda y deforma el
// layout del grafico.
export function truncarNombre(nombre) {
    if (!nombre) return "";
    return nombre.length > MAX_CARACTERES_NOMBRE
        ? `${nombre.slice(0, MAX_CARACTERES_NOMBRE)}...`
        : nombre;
}

// "EMITIDO" -> "Emitido"
function capitalizar(texto) {
    if (!texto) return "";
    const minusculas = String(texto).toLowerCase();
    return minusculas.charAt(0).toUpperCase() + minusculas.slice(1);
}

// "2026-08-27" -> Date local (evita el corrimiento de dia que da
// "new Date('2026-08-27')" al interpretarse como UTC en un huso horario negativo).
function parsearFechaLocal(fechaStr) {
    const [anio, mes, dia] = fechaStr.slice(0, 10).split("-").map(Number);
    return new Date(anio, mes - 1, dia);
}

function obtenerClaveTiempo(fecha, esMismoMes) {
    return esMismoMes
        ? `${fecha.getDate()} ${NOMBRES_MES_CORTO[fecha.getMonth()]}`
        : `${NOMBRES_MES_CORTO[fecha.getMonth()]} ${fecha.getFullYear()}`;
}

// Linea de tiempo armada solo con el filtro (dias del mes, o meses del
// rango). Se usa cuando el servicio no devuelve historial, para que el
// grafico igual pinte sus ejes con la serie en 0 en vez de quedar en blanco.
function construirLineaTiempoVacia(filter, esMismoMes) {
    const anioInicio = Number(filter.anioInicio);
    const mesInicio = Number(filter.mesInicio);
    const anioFin = Number(filter.anioFin);
    const mesFin = Number(filter.mesFin);

    if (esMismoMes) {
        const diasDelMes = new Date(anioInicio, mesInicio, 0).getDate();
        return Array.from({ length: diasDelMes }, (_, indice) => `${indice + 1} ${NOMBRES_MES_CORTO[mesInicio - 1]}`);
    }

    const claves = [];
    let anio = anioInicio;
    let mes = mesInicio;
    while ((anio < anioFin || (anio === anioFin && mes <= mesFin)) && claves.length < 60) {
        claves.push(`${NOMBRES_MES_CORTO[mes - 1]} ${anio}`);
        mes += 1;
        if (mes > 12) {
            mes = 1;
            anio += 1;
        }
    }
    return claves.length > 0 ? claves : [`${NOMBRES_MES_CORTO[mesInicio - 1]} ${anioInicio}`];
}

// Arma "Ventas vs Mes Anterior" a partir del historial diario del servicio
// (montoSoles/montoDolares y sus pares "Anterior"). Se agrupa por dia si el
// filtro cae en un mismo mes, o por mes si abarca mas de uno.
function construirComparativo(historial, filter, esMismoMes) {
    const filas = historial
        .filter((fila) => fila && fila.fecha)
        .map((fila) => ({ ...fila, __fecha: parsearFechaLocal(fila.fecha) }))
        .sort((a, b) => a.__fecha - b.__fecha);

    const ordenClaves = [];
    const acumulado = {};
    filas.forEach((fila) => {
        const clave = obtenerClaveTiempo(fila.__fecha, esMismoMes);
        if (!acumulado[clave]) {
            acumulado[clave] = { pen: 0, usd: 0, penAnterior: 0, usdAnterior: 0 };
            ordenClaves.push(clave);
        }
        acumulado[clave].pen += Number(fila.montoSoles) || 0;
        acumulado[clave].usd += Number(fila.montoDolares) || 0;
        acumulado[clave].penAnterior += Number(fila.montoSolesAnterior) || 0;
        acumulado[clave].usdAnterior += Number(fila.montoDolaresAnterior) || 0;
    });

    const timeLine = ordenClaves.length > 0 ? ordenClaves : construirLineaTiempoVacia(filter, esMismoMes);
    const construirSerie = (campo) => timeLine.map((clave) => Number(((acumulado[clave] || {})[campo] || 0).toFixed(2)));

    return {
        timeLine,
        pen: { actual: construirSerie("pen"), anterior: construirSerie("penAnterior") },
        usd: { actual: construirSerie("usd"), anterior: construirSerie("usdAnterior") },
    };
}

function direccionTendencia(actual, anterior) {
    if (actual > anterior) return "up";
    if (actual < anterior) return "down";
    return "flat";
}

// % de variacion actual vs anterior. Si el periodo anterior no tuvo
// movimiento, se evita la division entre 0: sin cambio real (0/0) queda
// plano, y una aparicion nueva (valor/0) se marca como +100%.
function calcularTendenciaPct(actual, anterior) {
    if (!anterior) return actual > 0 ? 100 : 0;
    return ((actual - anterior) / anterior) * 100;
}

// Si el servicio no manda el dato (o no manda su par "Anterior"), el KPI
// queda en 0 en vez de romper la tarjeta.
function construirKpi(actual, anterior) {
    const valorActual = Number(actual) || 0;
    const valorAnterior = Number(anterior) || 0;
    return {
        valor: valorActual,
        tendenciaPct: calcularTendenciaPct(valorActual, valorAnterior),
        tendenciaDireccion: direccionTendencia(valorActual, valorAnterior),
    };
}

function dividir(numerador, denominador) {
    const divisor = Number(denominador) || 0;
    return divisor > 0 ? (Number(numerador) || 0) / divisor : 0;
}

// Agrupa las filas por nombre y devuelve cada grupo con su total y su % de
// participacion, de mayor a menor. Se omiten los grupos sin monto.
function construirDistribucion(filas, campoNombre, campoTotal) {
    const acumulado = {};
    filas.forEach((fila) => {
        const nombre = fila[campoNombre] || "Sin nombre";
        acumulado[nombre] = (acumulado[nombre] || 0) + (Number(fila[campoTotal]) || 0);
    });

    const totalGeneral = Object.keys(acumulado).reduce((suma, nombre) => suma + acumulado[nombre], 0);
    return Object.keys(acumulado)
        .map((nombre) => ({
            nombre,
            total: acumulado[nombre],
            porcentaje: totalGeneral > 0 ? (acumulado[nombre] / totalGeneral) * 100 : 0,
        }))
        .filter((item) => item.total > 0)
        .sort((a, b) => b.total - a.total);
}

function construirCategorias(categoriasApi, campoTotal) {
    return construirDistribucion(categoriasApi, "nombre", campoTotal)
        .map((item, indice) => ({ ...item, color: COLORES_CATEGORIA[indice % COLORES_CATEGORIA.length] }));
}

function construirEstablecimientos(establecimientosApi, campoTotal) {
    return construirDistribucion(establecimientosApi, "establecimiento", campoTotal)
        .map((item, indice) => ({ ...item, ...ESTILOS_ESTABLECIMIENTO[indice % ESTILOS_ESTABLECIMIENTO.length] }));
}

function construirEstados(estadosApi) {
    let indiceOtros = 0;
    return construirDistribucion(estadosApi, "nombre", "total").map((item) => {
        let color = COLORES_ESTADO[String(item.nombre).trim().toUpperCase()];
        if (!color) {
            color = COLORES_ESTADO_OTROS[indiceOtros % COLORES_ESTADO_OTROS.length];
            indiceOtros += 1;
        }
        return { ...item, nombre: capitalizar(item.nombre), color };
    });
}

// Top 5 de clientes. El servicio manda el detalle de ventas por cliente
// (nombre, fecha, totalSoles, totalDolares) sin la cantidad de comprobantes,
// asi que "Cpe" se cuenta como el numero de filas de cada cliente.
// "% del Total" se calcula contra el total de ventas del KPI; si no viene,
// contra la suma del propio detalle.
function construirTopClientes(detalle, campoTotal, totalVentasKpi) {
    const acumulado = {};
    detalle.forEach((fila) => {
        const nombre = fila.nombre || "Sin nombre";
        const previo = acumulado[nombre] || { ventas: 0, cpe: 0 };
        previo.ventas += Number(fila[campoTotal]) || 0;
        previo.cpe += 1;
        acumulado[nombre] = previo;
    });

    const clientes = Object.keys(acumulado)
        .map((nombre) => ({ nombre, ...acumulado[nombre] }))
        .sort((a, b) => b.ventas - a.ventas);

    const sumaDetalle = clientes.reduce((suma, item) => suma + item.ventas, 0);
    const totalReferencia = totalVentasKpi > 0 ? totalVentasKpi : sumaDetalle;
    const ventaMaxima = clientes.length > 0 ? clientes[0].ventas : 0;

    return clientes.slice(0, 5).map((item) => ({
        ...item,
        porcentaje: dividir(item.ventas, totalReferencia) * 100,
        // Ancho de la barra: relativo al cliente con mas ventas.
        avance: Math.min(100, dividir(item.ventas, ventaMaxima) * 100),
        cpePromedio: dividir(item.ventas, item.cpe),
    }));
}

export function buildDashboardData(response, filter) {
    // El servicio (comprobanteService.dashboardResumen) devuelve:
    // - kpis: arreglo de un solo elemento con los totales del periodo y su
    //   comparacion contra el periodo anterior (sufijo "Anterior").
    // - historial: ventas por dia del periodo y del periodo anterior.
    // - categorias / establecimientos: totales en soles y dolares.
    // - estados: cantidad de comprobantes por estado.
    // - clientes: detalle de ventas por cliente y fecha.
    // Cualquier bloque ausente se trata como vacio para que el dashboard
    // muestre ceros en lugar de quedar en blanco.
    const kpisApi = (response && response.kpis && response.kpis[0]) || {};
    const historial = (response && response.historial) || [];
    const categoriasApi = (response && response.categorias) || [];
    const establecimientosApi = (response && response.establecimientos) || [];
    const estadosApi = (response && response.estados) || [];
    const detalleClientes = (response && response.clientes) || [];

    const esMismoMes = filter.anioInicio === filter.anioFin && filter.mesInicio === filter.mesFin;

    const totalSoles = Number(kpisApi.totalVentasSoles) || 0;
    const totalDolares = Number(kpisApi.totalVentasDolares) || 0;

    return {
        esMismoMes,
        kpis: {
            ventas: {
                pen: construirKpi(kpisApi.totalVentasSoles, kpisApi.totalVentasSolesAnterior),
                usd: construirKpi(kpisApi.totalVentasDolares, kpisApi.totalVentasDolaresAnterior),
            },
            comprobantes: construirKpi(kpisApi.cantidadCPE, kpisApi.cantidadCPEAnterior),
            clientesNuevos: construirKpi(kpisApi.clientesNuevos, kpisApi.clientesNuevosAnterior),
            // Venta promedio por comprobante: total de ventas / cantidad de CPE.
            cpePromedio: {
                pen: construirKpi(
                    dividir(kpisApi.totalVentasSoles, kpisApi.cantidadCPE),
                    dividir(kpisApi.totalVentasSolesAnterior, kpisApi.cantidadCPEAnterior)
                ),
                usd: construirKpi(
                    dividir(kpisApi.totalVentasDolares, kpisApi.cantidadCPE),
                    dividir(kpisApi.totalVentasDolaresAnterior, kpisApi.cantidadCPEAnterior)
                ),
            },
        },
        comparativo: construirComparativo(historial, filter, esMismoMes),
        categorias: {
            pen: construirCategorias(categoriasApi, "totalSoles"),
            usd: construirCategorias(categoriasApi, "totalDolares"),
        },
        establecimientos: {
            pen: construirEstablecimientos(establecimientosApi, "totalSoles"),
            usd: construirEstablecimientos(establecimientosApi, "totalDolares"),
        },
        topClientes: {
            pen: construirTopClientes(detalleClientes, "totalSoles", totalSoles),
            usd: construirTopClientes(detalleClientes, "totalDolares", totalDolares),
        },
        estados: construirEstados(estadosApi),
    };
}
